import React, { createContext, useEffect, useState, FC, PropsWithChildren } from 'react';

import isMobile from '../helpers/isMobile';
import useMounted from '../hooks/useMounted';
import { isProductTemporarilyUnavailable } from '../helpers/productAvailability';

const CART_KEY = 'amanita-cart-v3';

export type CartItem = {
  name: string;
  slug: string;
  units: string;
  price: string;
  quantity: number;
};

type CartActions = 'add' | 'remove' | 'remove-all';

const isCartItem = (value: unknown): value is CartItem => {
  if (!value || typeof value !== 'object') return false;

  return (
    'name' in value && typeof value.name === 'string' && value.name.trim().length > 0 &&
    'slug' in value && typeof value.slug === 'string' && value.slug.trim().length > 0 &&
    'units' in value && typeof value.units === 'string' && value.units.trim().length > 0 &&
    'price' in value && typeof value.price === 'string' && value.price.trim().length > 0 &&
    Number.isFinite(Number(value.price)) && Number(value.price) > 0 &&
    'quantity' in value && typeof value.quantity === 'number' && Number.isInteger(value.quantity) && value.quantity > 0
  );
};

type CartContextProps = {
  cart: CartItem[];
  totalPrice: number;
  totalItems: number;
  shopProductCount: number;
  handleShowMore(): void;
  setCartData(item: CartItem, action: CartActions): void;
  setCartFromInvoice(items: CartItem[]): void;
};

export const CartContext = createContext({} as CartContextProps);

const CartProvider: FC<PropsWithChildren> = ({ children }) => {
  const isMounted = useMounted();
  const [cart, updateCart] = useState<CartItem[]>([]);

  const shopProductsStep = !isMounted ? 6 : isMobile() ? 6 : 9;
  const [shopProductCount, setShopProductCount] = useState(shopProductsStep);

  const handleShowMore = () => {
    setShopProductCount((prevCount) => prevCount + shopProductsStep);
  };

  const totalItems = cart.reduce((acc, item) => acc + item.quantity, 0);

  const totalPrice = cart.reduce((acc, item) => acc + item.quantity * +item.price, 0);

  const persistCart = (items: CartItem[]) => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(items));
    } catch {
      // Keep the in-memory cart usable when browser storage is unavailable.
    }
  };

  const getCartData = (): CartItem[] => {
    let rawData: string | null;
    try {
      rawData = localStorage.getItem(CART_KEY);
    } catch {
      return cart;
    }

    let parsedData: unknown;
    try {
      parsedData = rawData ? JSON.parse(rawData) : [];
    } catch {
      parsedData = [];
    }
    const availableItems = Array.isArray(parsedData)
      ? parsedData.filter(isCartItem).filter((item) => !isProductTemporarilyUnavailable(item.slug))
      : [];

    if (rawData !== JSON.stringify(availableItems)) {
      persistCart(availableItems);
    }

    return availableItems;
  };

  const setCartData = (newItem: CartItem, action: CartActions) => {
    const parsedData = getCartData();

    if (!isCartItem(newItem)) {
      updateCart(parsedData);
      return;
    }

    if (action === 'add' && isProductTemporarilyUnavailable(newItem.slug)) {
      updateCart(parsedData);
      return;
    }

    const cartProductIndex = parsedData.findIndex((item) => item.price === newItem.price && item.name === newItem.name);

    if (action !== 'add' && cartProductIndex === -1) {
      updateCart(parsedData);
      return;
    }

    if (action === 'add') {
      if (cartProductIndex !== -1) parsedData[cartProductIndex].quantity += newItem.quantity;
      else parsedData.push(newItem);
    } else if (action === 'remove') {
      if (parsedData[cartProductIndex].quantity <= 1) {
        if (confirm(`Do you want to remove it?`)) {
          parsedData.splice(cartProductIndex, 1);
        }
      } else parsedData[cartProductIndex].quantity -= 1;
    } else if (action === 'remove-all') {
      if (
        cartProductIndex !== -1 &&
        confirm(`Do you want to remove this, ${parsedData[cartProductIndex].quantity} items?`)
      )
        parsedData.splice(cartProductIndex, 1);
    }

    updateCart(parsedData);
    persistCart(parsedData);
  };

  const setCartFromInvoice = (items: CartItem[]) => {
    const availableItems = items.filter(isCartItem).filter((item) => !isProductTemporarilyUnavailable(item.slug));
    updateCart(availableItems);
    persistCart(availableItems);
  };

  const saveCartOnChanges = () => {
    const newData = getCartData();
    newData && updateCart(newData);
  };

  useEffect(() => {
    const newData = getCartData();
    newData && updateCart(newData);

    window.addEventListener('storage', saveCartOnChanges);

    return () => window.removeEventListener('storage', saveCartOnChanges);
  }, []);

  return (
    <CartContext.Provider value={{ cart, totalItems, totalPrice, setCartData, setCartFromInvoice, shopProductCount, handleShowMore }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
